import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useBookings, attendeeOutcome, type Attendee, type BookedLesson } from '../api/use-bookings.js';
import { athleteLessonLink } from '../api/lesson-link.js';

/**
 * One person's lessons. On a child's page (stage 4b, mockup *Signature in the
 * Portal*): their next lesson and their latest one, then All lessons, which
 * opens the whole list in place. On the holder's own page: the whole list, as
 * before. Nothing is said when there are none; a failed read says so.
 *
 * A clinic has its own Next clinic, on top of Next lesson when it comes first in
 * time, and any clinic's row leads with its title (Matthew, 31 October).
 */
export function PersonLessons({ clientId, condensed }: { clientId: string; condensed: boolean }) {
  const { state } = useBookings();
  const [all, setAll] = useState(false);

  if (state.status === 'loading') return null;
  if (state.status === 'failed') {
    return (
      <>
        <span className="section-label">Lessons</span>
        <p className="empty">{state.message}</p>
      </>
    );
  }

  const theirs = state.lessons.flatMap((lesson) =>
    lesson.attendees
      .filter((attendee) => attendee.client_id === clientId && attendee.lesson_id)
      .map((attendee) => ({ lesson, attendee })),
  );
  if (theirs.length === 0) return null;

  const list = (
    <>
      <span className="section-label">Lessons</span>
      {theirs.map((each) => (
        <LessonRow key={each.attendee.lesson_id} {...each} />
      ))}
    </>
  );
  if (!condensed) return list;

  const now = Date.now();
  const coming = theirs
    .filter(({ lesson, attendee }) => attendeeOutcome(lesson, attendee) === 'Booked' && Date.parse(lesson.slot_start) > now)
    .sort((a, b) => Date.parse(a.lesson.slot_start) - Date.parse(b.lesson.slot_start));
  const next = coming.find(({ lesson }) => !lesson.is_clinic);
  const nextClinic = coming.find(({ lesson }) => lesson.is_clinic);
  const clinicFirst =
    nextClinic !== undefined && (!next || Date.parse(nextClinic.lesson.slot_start) < Date.parse(next.lesson.slot_start));
  const clinicRow = nextClinic ? (
    <>
      <span className="section-label">Next clinic</span>
      <LessonRow {...nextClinic} />
    </>
  ) : null;
  const latest = theirs
    .filter(({ lesson, attendee }) => attendeeOutcome(lesson, attendee) === 'Completed')
    .sort((a, b) => Date.parse(b.lesson.slot_start) - Date.parse(a.lesson.slot_start))[0];

  return (
    <>
      {clinicFirst ? clinicRow : null}
      {next ? (
        <>
          <span className="section-label">Next lesson</span>
          <LessonRow {...next} />
        </>
      ) : null}
      {clinicFirst ? null : clinicRow}
      {latest ? (
        <>
          <span className="section-label">Last lesson</span>
          <LessonRow {...latest} />
        </>
      ) : null}
      <button type="button" className="link-button all-lessons" aria-expanded={all} onClick={() => setAll(!all)}>
        All lessons ›
      </button>
      {all ? list : null}
    </>
  );
}

function LessonRow({ lesson, attendee }: { lesson: BookedLesson; attendee: Attendee }) {
  const outcome = attendeeOutcome(lesson, attendee);
  return (
    <Link className="hour-row" to={athleteLessonLink(lesson.booking_id, attendee.lesson_id ?? '', true)}>
      <span className="hour-row-text">
        <span className="name">{lesson.is_clinic ? lesson.product_name : when(lesson.slot_start)}</span>
        <span className="detail">
          {[lesson.is_clinic ? when(lesson.slot_start) : '', `Coach ${lesson.coach_name}`, lesson.is_clinic ? '' : lesson.product_name]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
      <span className={`outcome ${outcome.replace(' ', '-').toLowerCase()}`}>{outcome}</span>
      <span className="chevron" aria-hidden="true">
        ›
      </span>
    </Link>
  );
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}
